import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import axios from "axios";
import { API } from "../../../config";

import {
  setAdvancedDeclarationOption,
  setListOfSpeciesTemplates,
  setListOfStoragesTemplates,
  setLocationGrainTracker,
  setQualityCriteriaData,
} from "../../slices/grainTracker/grainTracker";

export const mockFeatures = {
  properties: {
    full_address: "unknown",
    context: {
      country: {
        name: "unknown",
      },
    },
  },
};

export const fetchLocationMapbox = async (
  token: string,
  locale: string,
  longitude: number,
  latitude: number,
): Promise<any> => {
  try {
    const res = await axios({
      method: "GET",
      url: `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
      headers: {
        "Accept-Language": `${locale}`,
        Authorization: `Bearer ${token}`,
      },
    });
    const { data } = res;
    return data.features.length > 0 ? data.features[0] : mockFeatures;
  } catch (error) {
    return error;
  }
};

export const fetchAllotmentPoliciesEachBin = async (
  token: string,
  locale: string,
  orgGeoPointId: number,
  binId: string,
): Promise<any> => {
  try {
    const res = await axios({
      method: "GET",
      url: `${API}/storages/storage-instance?orgGeoPointId=${orgGeoPointId}&$eager=[allotmentPolicies]&fetchAllotmentValueArray=true`,
      headers: {
        "Accept-Language": `${locale}`,
        Authorization: `Bearer ${token}`,
      },
    });
    const { data } = res;

    const eachBing = data.data.filter((value: any) => value.id === binId)[0]
      .allotmentPolicies;
    return eachBing;
  } catch (error) {
    return error;
  }
};

export const fetchStoragesOfEachLocation = createAsyncThunk(
  "GrainWiseReducer/fetchStoragesOfEachLocation",
  async (arg: { token: string; orgGeoPointId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { locationGrainTrackerValues } = (store.getState() as RootState)
      .GrainWiseReducer;

    try {
      const response = await axios.get(
        `${API}/storages/storage-instance?orgGeoPointId=${arg.orgGeoPointId}&$eager=[allotmentPolicies]&fetchAllotmentValueArray=true`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setLocationGrainTracker({
          ...locationGrainTrackerValues,
          storagesList: data.data,
          storagesTotal: data.total,
          error: "",
        }),
      );

      return data;
    } catch (error: any) {
      return store.dispatch(
        setLocationGrainTracker({
          ...locationGrainTrackerValues,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);

export const fetchStorageTemplate = createAsyncThunk(
  "GrainWiseReducer/fetchStorageTemplate",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { listOfStoragesTemplates } = (store.getState() as RootState)
      .GrainWiseReducer;

    store.dispatch(
      setListOfStoragesTemplates({
        ...listOfStoragesTemplates,
        loading: true,
        error: "",
      }),
    );

    try {
      const response = await axios.get(`${API}/storages/storage-template`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      store.dispatch(
        setListOfStoragesTemplates({
          ...listOfStoragesTemplates,
          storagesTemplates: data,
          loading: false,
          error: "",
        }),
      );
      return data;
    } catch (error: any) {
      return store.dispatch(
        setListOfStoragesTemplates({
          ...listOfStoragesTemplates,
          loading: false,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);

export const fetchSpeciesTemplate = createAsyncThunk(
  "GrainWiseReducer/fetchSpeciesTemplate",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { listOfSpecies } = (store.getState() as RootState).GrainWiseReducer;

    store.dispatch(
      setListOfSpeciesTemplates({
        ...listOfSpecies,
        loading: true,
        error: "",
      }),
    );

    try {
      // hardcoded ids provided by Artemis
      const hardcodedIds = [4, 1, 3, 2, 20, 21, 18, 22, 7, 8, 10, 9, 11, 19];
      const idsQuery = hardcodedIds.map((x) => `id[$in][]=${x}`).join("&");
      const response = await axios.get(
        `${API}/core/cereal-type?${idsQuery}&$select[0]=id&$select[1]=slug&$select[2]=textId&$select[3]=conversionFromWheatTons`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setListOfSpeciesTemplates({
          ...listOfSpecies,
          speciesTemplates: data,
          loading: false,
          error: "",
        }),
      );

      return data;
    } catch (error: any) {
      return store.dispatch(
        setListOfSpeciesTemplates({
          ...listOfSpecies,
          loading: false,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);

export const fetchQualityCriteria = createAsyncThunk(
  "GrainWiseReducer/fetchQualityCriteria",
  async (arg: { token: string; specie: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { qualityCriteria } = (store.getState() as RootState)
      .GrainWiseReducer;

    store.dispatch(
      setQualityCriteriaData({ ...qualityCriteria, loading: true, error: "" }),
    );
    try {
      const response = await axios.get(
        `${API}/storages/storage-quality-criterion?$eager=[qualityCriterionUnit]&cerealTypeId=${arg.specie}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setQualityCriteriaData({
          ...qualityCriteria,
          loading: false,
          data: data.data,
          total: data.total,
          limit: data.limit,
        }),
      );

      return data;
    } catch (error: any) {
      store.dispatch(
        setQualityCriteriaData({
          ...qualityCriteria,
          loading: false,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);

export const advancedDeclarationOption = createAsyncThunk(
  "GrainWiseReducer/advancedDeclarationOption",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const { advancedDeclarationOption, addingCriteria } = (
      store.getState() as RootState
    ).GrainWiseReducer;

    const { searchText } = advancedDeclarationOption;
    const { values } = addingCriteria;
    const managingTag = values.allotmentValueArray ?? [""];
    const queryString = managingTag
      .map((tag) => `tags[$contains][]=${tag}`)
      .join("&");

    const token = user?.token ?? "";

    store.dispatch(
      setAdvancedDeclarationOption({
        ...advancedDeclarationOption,
        error: "",
      }),
    );

    try {
      const response = await axios.get(
        `${API}/labels/advanced-declaration-option?$modify[search][0]=${searchText}&$modify[search][1]=${locale}&validated=true&$select[0]=id&$select[1]=slug&$select[2]=defaultCustomerFacingText&$select[3]=customerFacingTextId&${queryString}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setAdvancedDeclarationOption({
          ...advancedDeclarationOption,
          tagsResults: data.data,
          loading: false,
        }),
      );

      return data;
    } catch (error: any) {
      store.dispatch(
        setAdvancedDeclarationOption({
          ...advancedDeclarationOption,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);
